/**
 * Splits the founder's welcome text (welcome_config.full_message, edited by the
 * owner in admin → Welcome Messages) into letter parts for /welcome.
 *
 * The stored text is one free-form string: greeting, a title line, an opening
 * quote, paragraphs, a sign-off, a motto, contact lines, and then venue policy
 * updates. Nothing here changes the words; it only decides how each paragraph is
 * shown. Anything that doesn't match a rule stays a normal paragraph, so owner
 * edits can never make text disappear.
 */

export type LetterBlock =
  | { kind: 'para'; text: string }
  | { kind: 'emphasis'; lines: string[] }
  | { kind: 'quote'; lines: string[] };

export interface ContactLine { label: string; value: string; href?: string }

export interface FounderLetter {
  greeting: string | null;
  headline: string | null;
  blocks: LetterBlock[];
  closing: string | null;
  signature: string | null;
  motto: string | null;
  contacts: ContactLine[];
  /** Everything after the contact lines (policy updates), as paragraphs. */
  appendix: string[];
  /** The appendix grouped into policies: a new group starts at each "Update…" paragraph. */
  policies: { title: string; body: string[] }[];
}

const QUOTE_OPEN = /^["“‘']/;
const QUOTE_CLOSE = /["”’']$/;
const CLOSING = /^(warm(est)? regards|regards|with (love|warmth|gratitude)|sincerely|yours( truly| sincerely)?|best( wishes)?)[,.!]?$/i;
const GREETING = /^(hello|hi|dear\b.*|namaskaram|greetings)[,!.]?$/i;

const splitParas = (text: string) =>
  text.replace(/\r\n/g, '\n').split(/\n\s*\n/).map(p => p.split('\n').map(l => l.trim()).filter(Boolean)).filter(p => p.length);

function contactFrom(line: string): ContactLine | null {
  const m = line.match(/^([^:]{2,30}):\s*(.+)$/);
  if (!m) return null;
  const label = m[1].trim();
  const value = m[2].trim();
  if (/^https?:\/\//i.test(value)) return { label, value: value.replace(/^https?:\/\//i, '').replace(/\/$/, ''), href: value };
  if (/(mobile|phone|tel|whatsapp)/i.test(label)) return { label, value, href: `tel:${value.replace(/[^\d+]/g, '')}` };
  if (/@/.test(value)) return { label, value, href: `mailto:${value}` };
  return { label, value };
}

export function parseFounderLetter(raw: string | null | undefined): FounderLetter {
  const out: FounderLetter = { greeting: null, headline: null, blocks: [], closing: null, signature: null, motto: null, contacts: [], appendix: [], policies: [] };
  const paras = splitParas(raw || '');
  let i = 0;

  if (paras[i] && paras[i].length === 1 && GREETING.test(paras[i][0])) out.greeting = paras[i++][0];
  if (paras[i] && paras[i].length === 1 && paras[i][0].length <= 90 && !/[.!?]$/.test(paras[i][0]) && !QUOTE_OPEN.test(paras[i][0])) {
    out.headline = paras[i++][0];
  }

  for (; i < paras.length; i++) {
    const p = paras[i];
    const first = p[0];
    if (CLOSING.test(first)) {
      out.closing = first;
      out.signature = p[1] || null;
      i++;
      break;
    }
    const joined = p.join(' ');
    if (QUOTE_OPEN.test(first) && QUOTE_CLOSE.test(p[p.length - 1])) {
      out.blocks.push({ kind: 'quote', lines: p.map((l, k) => (k === 0 ? l.replace(QUOTE_OPEN, '') : l)).map((l, k) => (k === p.length - 1 ? l.replace(QUOTE_CLOSE, '') : l)) });
    } else if (joined.length <= 70 || (p.length > 1 && p.every(l => l.length <= 60))) {
      out.blocks.push({ kind: 'emphasis', lines: p });
    } else {
      out.blocks.push({ kind: 'para', text: joined });
    }
  }

  // After the sign-off: optional quoted motto, then contact lines, then the rest.
  if (paras[i] && paras[i].length === 1 && QUOTE_OPEN.test(paras[i][0]) && QUOTE_CLOSE.test(paras[i][0])) {
    out.motto = paras[i++][0].replace(QUOTE_OPEN, '').replace(QUOTE_CLOSE, '');
  }
  if (paras[i]) {
    const lines = paras[i];
    const parsed = lines.map(contactFrom);
    if (parsed.some(Boolean)) {
      lines.forEach((l, k) => out.contacts.push(parsed[k] || { label: '', value: l }));
      i++;
    }
  }
  for (; i < paras.length; i++) out.appendix.push(paras[i].join('\n'));
  out.policies = groupPolicies(out.appendix);

  // No sign-off found: the whole text was treated as blocks; keep it all visible.
  return out;
}

/** Short label-like paragraph (e.g. "Food Arrangement Policy\nHigh"): becomes the next group's title. */
const isHeading = (p: string) => p.split('\n').every(l => l.length <= 60 && !/[.:!?]$/.test(l));

export function groupPolicies(paras: string[]): { title: string; body: string[] }[] {
  const groups: { title: string; body: string[] }[] = [];
  let heading: string | null = null;
  for (const p of paras) {
    if (isHeading(p)) { heading = heading ? `${heading} · ${p.split('\n').join(' · ')}` : p.split('\n').join(' · '); continue; }
    if (/^update\b/i.test(p) || !groups.length) {
      groups.push(heading ? { title: heading, body: [p] } : { title: p.split('\n')[0], body: p.split('\n').slice(1).length ? [p.split('\n').slice(1).join('\n')] : [] });
      heading = null;
    } else {
      groups[groups.length - 1].body.push(p);
    }
  }
  if (heading) groups.push({ title: heading, body: [] });
  return groups;
}
